import { MenuItem } from "@mui/material";
import { MouseEvent, useState } from "react";
import { BULK_ACTIONS_HIDDEN_TABS } from "../constants";
import { PrinterInfo } from "../services/actionsApi";
import { StatusAction } from "../types";
import PermissionComponent, {
  AnyPermission,
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";
import {
  ExportAllRecords,
  ExportIcon,
  SelectedRecords,
} from "Shared/Icons";
import ArrowBottomIcon from "Shared/SVG/ArrowBottomIcon";
import useLang from "Shared/hooks/useLanguage";
import {
  StyledDropButton,
  StyledDropMenu,
} from "Utils/Style/Dropdownstyle";

export type BulkAction =
  | StatusAction
  | "transferZoho"
  | "editDoc"
  | "printRecords"
  | "printReports"
  | "printRecordsAndReports"
  | "exportAll"
  | "exportExcel"
  | "clear";

interface Props {
  selectedCount: number;
  tabId: number;
  printers: PrinterInfo[];
  onAction: (action: BulkAction) => void;
  onPrintLabels: (printer: PrinterInfo) => void;
}

const BulkActionBar = ({
  selectedCount,
  tabId,
  printers,
  onAction,
  onPrintLabels,
}: Props) => {
  const { t } = useLang();
  const [anchorEl, setAnchorEl] = useState<Record<string, HTMLElement | null>>({});

  if (!selectedCount) return null;

  const showBulk = !BULK_ACTIONS_HIDDEN_TABS.includes(tabId);

  const handleClick = (e: MouseEvent<HTMLElement>, key: string) => {
    e.stopPropagation();
    setAnchorEl((prev) => ({ ...prev, [key]: e.currentTarget }));
  };
  const handleClose = (key: string) => {
    setAnchorEl((prev) => ({ ...prev, [key]: null }));
  };

  const arrow = (
    <span className="svg-icon svg-icon-5 m-0">
      <ArrowBottomIcon />
    </span>
  );

  return (
    <div className="vr2-bulkbar">
      <span className="fw-bold me-1">
        {selectedCount} {t("selected")}
      </span>

      {showBulk && (
        <>
          {/* Bulk Action dropdown (same as v1: single dropdown with status + more + print) */}
          <div>
            <StyledDropButton
              id="ViewRequisitionV2BulkAction"
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl.bulk)}
              onClick={(e) => handleClick(e, "bulk")}
              className="btn btn-info btn-sm"
            >
              {t("Bulk Action")}
              {arrow}
            </StyledDropButton>
            <StyledDropMenu
              anchorEl={anchorEl.bulk}
              open={Boolean(anchorEl.bulk)}
              onClose={() => handleClose("bulk")}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <div className="d-flex flex-wrap">
                <div style={{ minWidth: "150px" }}>
                  <PermissionComponent
                    moduleName="Requisition"
                    pageName="View Requisition v2"
                    permissionIdentifier="Completed"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <MenuItem className="p-0">
                        <a
                          className="p-0 text-dark w-100"
                          onClick={() => {
                            handleClose("bulk");
                            onAction(StatusAction.Complete);
                          }}
                        >
                          {getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Complete")}
                        </a>
                      </MenuItem>
                    )}
                  </PermissionComponent>
                  <PermissionComponent
                    moduleName="Requisition"
                    pageName="View Requisition v2"
                    permissionIdentifier="Deleted"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <MenuItem className="p-0">
                        <a
                          className="p-0 text-dark w-100"
                          onClick={() => {
                            handleClose("bulk");
                            onAction(StatusAction.Delete);
                          }}
                        >
                          {getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Delete")}
                        </a>
                      </MenuItem>
                    )}
                  </PermissionComponent>
                  <PermissionComponent
                    moduleName="Requisition"
                    pageName="View Requisition v2"
                    permissionIdentifier="OnHold"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <MenuItem className="p-0">
                        <a
                          className="p-0 text-dark w-100"
                          onClick={() => {
                            handleClose("bulk");
                            onAction(StatusAction.Hold);
                          }}
                        >
                          {getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Hold")}
                        </a>
                      </MenuItem>
                    )}
                  </PermissionComponent>
                  <PermissionComponent
                    moduleName="Requisition"
                    pageName="View Requisition v2"
                    permissionIdentifier="Rejected"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <MenuItem className="p-0">
                        <a
                          className="p-0 text-dark w-100"
                          onClick={() => {
                            handleClose("bulk");
                            onAction(StatusAction.Reject);
                          }}
                        >
                          {getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Reject")}
                        </a>
                      </MenuItem>
                    )}
                  </PermissionComponent>
                  <PermissionComponent
                    moduleName="Requisition"
                    pageName="View Requisition v2"
                    permissionIdentifier="TransferOrderToZoho"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <MenuItem className="p-0">
                        <a
                          className="p-0 text-dark w-100"
                          onClick={() => {
                            handleClose("bulk");
                            onAction("transferZoho");
                          }}
                        >
                          {getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Transfer To Zoho")}
                        </a>
                      </MenuItem>
                    )}
                  </PermissionComponent>
                  <PermissionComponent
                    moduleName="Requisition"
                    pageName="View Requisition v2"
                    permissionIdentifier="EditDateofCollection"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <MenuItem className="p-0">
                        <a
                          className="p-0 text-dark w-100"
                          onClick={() => {
                            handleClose("bulk");
                            onAction("editDoc");
                          }}
                        >
                          {getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Edit Date of Collection")}
                        </a>
                      </MenuItem>
                    )}
                  </PermissionComponent>
                </div>

                <div style={{ minWidth: "200px" }}>
                  <PermissionComponent
                    moduleName="Requisition"
                    pageName="View Requisition v2"
                    permissionIdentifier="PrintSelectedRecords"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <MenuItem className="p-0" style={{ width: "230px" }}>
                        <a
                          className="w-auto p-0 text-dark"
                          onClick={() => {
                            handleClose("bulk");
                            onAction("printRecords");
                          }}
                        >
                          {getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Print Selected Records")}
                        </a>
                      </MenuItem>
                    )}
                  </PermissionComponent>
                  <PermissionComponent
                    moduleName="Requisition"
                    pageName="View Requisition v2"
                    permissionIdentifier="PrintSelectedReports"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <MenuItem className="p-0" style={{ width: "230px" }}>
                        <a
                          className="w-auto p-0 text-dark"
                          onClick={() => {
                            handleClose("bulk");
                            onAction("printReports");
                          }}
                        >
                          {getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Print Selected Reports")}
                        </a>
                      </MenuItem>
                    )}
                  </PermissionComponent>
                  <PermissionComponent
                    moduleName="Requisition"
                    pageName="View Requisition v2"
                    permissionIdentifier="PrintselectedRecords&Reports"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <MenuItem className="p-0" style={{ width: "230px" }}>
                        <a
                          className="p-0 text-dark"
                          onClick={() => {
                            handleClose("bulk");
                            onAction("printRecordsAndReports");
                          }}
                        >
                          {getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Print Selected Records & Reports")}
                        </a>
                      </MenuItem>
                    )}
                  </PermissionComponent>
                </div>
              </div>
            </StyledDropMenu>
          </div>

          {/* Bulk Label Print dropdown */}
          <PermissionComponent
            moduleName="Requisition"
            pageName="View Requisition v2"
            permissionIdentifier="PrintSelectedLabel"
          >
            <div>
              <StyledDropButton
                id="ViewRequisitionV2BulkLabelPrint"
                aria-haspopup="true"
                aria-expanded={Boolean(anchorEl.labels)}
                onClick={(e) => handleClick(e, "labels")}
                className="btn btn-warning btn-sm"
              >
                {t("Bulk Label Print")}
                {arrow}
              </StyledDropButton>
              <StyledDropMenu
                anchorEl={anchorEl.labels}
                open={Boolean(anchorEl.labels)}
                onClose={() => handleClose("labels")}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                className="w-auto"
              >
                {printers.map((p) => (
                  <MenuItem
                    key={p.value}
                    className="w-auto"
                    onClick={() => {
                      handleClose("labels");
                      onPrintLabels(p);
                    }}
                  >
                    <i className="fa fa fa-print text-warning mr-2 w-20px"></i>
                    {p.label}
                  </MenuItem>
                ))}
                {printers.length === 0 && (
                  <MenuItem disabled>{t("No printers configured")}</MenuItem>
                )}
              </StyledDropMenu>
            </div>
          </PermissionComponent>
        </>
      )}

      {/* Export dropdown (matches v1: both items permission-gated) */}
      <AnyPermission
        moduleName="Requisition"
        pageName="View Requisition v2"
        permissionIdentifiers={["ExportAllRecords", "ExportSelectedRecords"]}
      >
        <div>
          <StyledDropButton
            id="ViewRequisitionV2ExportSelected"
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl.exp)}
            onClick={(e) => handleClick(e, "exp")}
            className="btn btn-excle btn-sm"
          >
            <ExportIcon />
            {arrow}
          </StyledDropButton>
          <StyledDropMenu
            anchorEl={anchorEl.exp}
            open={Boolean(anchorEl.exp)}
            onClose={() => handleClose("exp")}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <PermissionComponent
              moduleName="Requisition"
              pageName="View Requisition v2"
              permissionIdentifier="ExportAllRecords"
            >
              {({ pageName, moduleName, permissionIdentifier }) => (
                <MenuItem className="p-0">
                  <a
                    className="p-0 text-dark w-100"
                    onClick={() => {
                      handleClose("exp");
                      onAction("exportAll");
                    }}
                  >
                    <ExportAllRecords />
                    {getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Export All Records")}
                  </a>
                </MenuItem>
              )}
            </PermissionComponent>
            <PermissionComponent
              moduleName="Requisition"
              pageName="View Requisition v2"
              permissionIdentifier="ExportSelectedRecords"
            >
              {({ pageName, moduleName, permissionIdentifier }) => (
                <MenuItem className="p-0">
                  <a
                    className="p-0 text-dark w-100"
                    onClick={() => {
                      handleClose("exp");
                      onAction("exportExcel");
                    }}
                  >
                    <SelectedRecords />
                    {getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Export Selected Records")}
                  </a>
                </MenuItem>
              )}
            </PermissionComponent>
          </StyledDropMenu>
        </div>
      </AnyPermission>

      <button
        type="button"
        className="btn btn-sm btn-link py-1 ms-auto"
        onClick={() => onAction("clear")}
      >
        {t("Clear selection")}
      </button>
    </div>
  );
};

export default BulkActionBar;
